import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SiBitcoin, SiEthereum, SiTether } from 'react-icons/si';
import { FaHome, FaExchangeAlt, FaWallet, FaCreditCard, FaHistory, FaSignOutAlt, FaQrcode, FaCopy } from 'react-icons/fa';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '../components/ThemeProvider';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return fallback;
};

const CRYPTO_ICONS = {
  BTC: <SiBitcoin className="text-orange-500" size={24} />,
  ETH: <SiEthereum className="text-blue-500" size={24} />,
  USDT: <SiTether className="text-green-500" size={24} />
};

const COLORS = ['#0066FF', '#10B981', '#F59E0B', '#EF4444'];

const formatMoney = (value = 0) => {
  const amount = Number(value) || 0;
  const sign = amount < 0 ? '-' : '';
  return `${sign}$${Math.abs(amount).toFixed(2)}`;
};

const pnlTextClass = (value = 0) => {
  const amount = Number(value) || 0;
  if (amount > 0) return 'text-green-500';
  if (amount < 0) return 'text-red-500';
  return 'text-muted-foreground';
};

const pnlGlowClass = (value = 0) => {
  const amount = Number(value) || 0;
  if (amount > 0) return 'pnl-positive-glow';
  if (amount < 0) return 'pnl-negative-glow';
  return '';
};

export default function Dashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [balances, setBalances] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [buyForm, setBuyForm] = useState({ crypto_type: 'BTC', amount: '', inputType: 'crypto' });
  const [sellForm, setSellForm] = useState({ crypto_type: 'BTC', amount: '', inputType: 'crypto' });
  const [transferForm, setTransferForm] = useState({ crypto_type: 'BTC', amount: '', wallet_address: '', inputType: 'crypto' });
  const [withdrawForm, setWithdrawForm] = useState({ card_id: '', amount: '' });
  const [cardForm, setCardForm] = useState({ card_number: '', card_holder: '', expiry_date: '', cvv: '' });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    } else if (user?.role === 'admin') {
      navigate('/admin');
    } else if (user) {
      fetchData();
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [balRes, txRes, pmRes] = await Promise.all([
        axios.get(`${API_URL}/user/balance`),
        axios.get(`${API_URL}/transactions`),
        axios.get(`${API_URL}/payment/methods`)
      ]);
      setBalances(balRes.data);
      setTransactions(txRes.data);
      setPaymentMethods(pmRes.data);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 16);
    return limited.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + (cleaned.length > 2 ? '/' + cleaned.slice(2, 4) : '');
    }
    return cleaned;
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!buyForm.amount || parseFloat(buyForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const price = balances.prices.find(p => p.symbol === buyForm.crypto_type)?.price || 0;
    let cryptoAmount = parseFloat(buyForm.amount);
    
    // If input is in USD, convert to crypto amount
    if (buyForm.inputType === 'usd') {
      cryptoAmount = parseFloat(buyForm.amount) / (price * 1.01);
    }
    
    const totalCost = cryptoAmount * price * 1.01;
    
    if (balances.fiat_balance < totalCost) {
      toast.error(`Insufficient balance. You need $${totalCost.toFixed(2)} but have $${balances.fiat_balance.toFixed(2)}`);
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('crypto_type', buyForm.crypto_type);
      formData.append('amount', cryptoAmount);
      
      await axios.post(`${API_URL}/crypto/buy`, formData);
      toast.success(`Successfully bought ${cryptoAmount.toFixed(8)} ${buyForm.crypto_type}!`);
      setBuyForm({ ...buyForm, amount: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Purchase failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSell = async (e) => {
    e.preventDefault();
    if (!sellForm.amount || parseFloat(sellForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const price = balances.prices.find(p => p.symbol === sellForm.crypto_type)?.price || 0;
    let cryptoAmount = parseFloat(sellForm.amount);
    
    // If input is in USD, convert to crypto amount
    if (sellForm.inputType === 'usd') {
      cryptoAmount = parseFloat(sellForm.amount) / (price * 0.99);
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('crypto_type', sellForm.crypto_type);
      formData.append('amount', cryptoAmount);
      
      const res = await axios.post(`${API_URL}/crypto/sell`, formData);
      const realizedPnl = res.data?.realized_pnl || 0;
      const pnlLabel = realizedPnl >= 0 ? `Profit: ${formatMoney(realizedPnl)}` : `Loss: ${formatMoney(realizedPnl)}`;
      toast.success(`Successfully sold ${cryptoAmount.toFixed(8)} ${sellForm.crypto_type}. ${pnlLabel}`);
      setSellForm({ ...sellForm, amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sale failed');
    } finally {
      setLoading(false);
    }
  };

  const NETWORK_FEES = { BTC: 0.0005, ETH: 0.005, USDT: 1.0 };
  const QUICK_PERCENTAGES = [
    { label: '20%', pct: 0.20 },
    { label: '50%', pct: 0.50 },
    { label: '75%', pct: 0.75 },
    { label: 'MAX', pct: 1.0 },
  ];

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const price = balances.prices.find(p => p.symbol === transferForm.crypto_type)?.price || 0;
    let cryptoAmount = parseFloat(transferForm.amount);
    
    if (transferForm.inputType === 'usd') {
      cryptoAmount = parseFloat(transferForm.amount) / price;
    }
    
    const fee = NETWORK_FEES[transferForm.crypto_type] || 0;
    const availBal = balances[`${transferForm.crypto_type.toLowerCase()}_balance`] || 0;
    
    if (availBal < cryptoAmount + fee) {
      toast.error(`Insufficient balance. Need ${(cryptoAmount + fee).toFixed(8)} ${transferForm.crypto_type} (incl. ${fee} network fee)`);
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('crypto_type', transferForm.crypto_type);
      formData.append('amount', cryptoAmount);
      formData.append('wallet_address', transferForm.wallet_address);
      
      await axios.post(`${API_URL}/crypto/transfer`, formData);
      toast.success(`Transfer of ${cryptoAmount.toFixed(8)} ${transferForm.crypto_type} submitted for approval!`);
      setTransferForm({ ...transferForm, amount: '', wallet_address: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Transfer failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawForm.card_id) {
      toast.error('Please select a card');
      return;
    }
    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('card_id', withdrawForm.card_id);
      formData.append('amount', parseFloat(withdrawForm.amount));
      
      await axios.post(`${API_URL}/payment/withdraw`, formData);
      toast.success('Withdrawal submitted for processing!');
      setWithdrawForm({ card_id: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Withdrawal failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('card_number', cardForm.card_number.replace(/\s/g, ''));
      formData.append('card_holder', cardForm.card_holder);
      formData.append('expiry_date', cardForm.expiry_date);
      formData.append('cvv', cardForm.cvv);
      
      await axios.post(`${API_URL}/payment/add-method`, formData);
      toast.success('Payment method added!');
      setCardForm({ card_number: '', card_holder: '', expiry_date: '', cvv: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/payment/delete/${cardId}`);
      toast.success('Card deleted successfully!');
      await fetchData(); // Refresh data to update UI
    } catch (error) {
      toast.error('Failed to delete card');
    } finally {
      setLoading(false);
    }
  };

  const getCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (/^4/.test(cleaned)) return 'VISA';
    if (/^5[1-5]/.test(cleaned)) return 'MASTERCARD';
    if (/^3[47]/.test(cleaned)) return 'AMEX';
    if (/^6(?:011|5)/.test(cleaned)) return 'DISCOVER';
    return 'CARD';
  };

  if (authLoading || !balances) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Generate chart data
  const portfolioData = [
    { name: 'Jan', value: balances.total_portfolio_value * 0.85 },
    { name: 'Feb', value: balances.total_portfolio_value * 0.90 },
    { name: 'Mar', value: balances.total_portfolio_value * 0.88 },
    { name: 'Apr', value: balances.total_portfolio_value * 0.95 },
    { name: 'May', value: balances.total_portfolio_value * 0.92 },
    { name: 'Now', value: balances.total_portfolio_value }
  ];

  const pieData = [
    { name: 'FIAT', value: balances.fiat_balance },
    { name: 'BTC', value: balances.btc_balance * (balances.prices.find(p => p.symbol === 'BTC')?.price || 0) },
    { name: 'ETH', value: balances.eth_balance * (balances.prices.find(p => p.symbol === 'ETH')?.price || 0) },
    { name: 'USDT', value: balances.usdt_balance }
  ].filter(item => item.value > 0);

  const performance = balances.portfolio_performance || {};
  const totalPnl = performance.total_pnl || 0;
  const realizedPnl = performance.realized_pnl || 0;
  const unrealizedPnl = performance.unrealized_pnl || 0;

  const getSellEstimate = () => {
    if (!sellForm.amount) return null;
    const price = balances.prices.find(p => p.symbol === sellForm.crypto_type)?.price || 0;
    const amount = parseFloat(sellForm.amount);
    if (!price || !amount || amount <= 0) return null;

    const cryptoAmount = sellForm.inputType === 'usd' ? amount / (price * 0.99) : amount;
    const asset = performance.assets?.[sellForm.crypto_type];
    const avgCost = asset?.avg_cost || price;
    const netValue = cryptoAmount * price * 0.99;
    const estimatedCostBasis = cryptoAmount * avgCost;

    return {
      cryptoAmount,
      netValue,
      estimatedPnl: netValue - estimatedCostBasis,
      avgCost,
    };
  };

  const setBuyPercentage = (pct) => {
    const price = balances.prices.find(p => p.symbol === buyForm.crypto_type)?.price || 0;
    const spendableUsd = Math.max(0, (balances.fiat_balance || 0) * pct);

    if (!price || !spendableUsd) {
      setBuyForm({...buyForm, amount: ''});
      return;
    }

    if (buyForm.inputType === 'usd') {
      setBuyForm({...buyForm, amount: spendableUsd.toFixed(2)});
      return;
    }

    setBuyForm({...buyForm, amount: (spendableUsd / (price * 1.01)).toFixed(8)});
  };

  const setSellPercentage = (pct) => {
    const price = balances.prices.find(p => p.symbol === sellForm.crypto_type)?.price || 0;
    const availableCrypto = balances[`${sellForm.crypto_type.toLowerCase()}_balance`] || 0;
    const cryptoAmount = Math.max(0, availableCrypto * pct);

    if (!cryptoAmount) {
      setSellForm({...sellForm, amount: ''});
      return;
    }

    if (sellForm.inputType === 'usd') {
      setSellForm({...sellForm, amount: (cryptoAmount * price * 0.99).toFixed(2)});
      return;
    }

    setSellForm({...sellForm, amount: cryptoAmount.toFixed(8)});
  };

  const getStatusBadge = (status) => {
    const classes = {
      'Completed': 'bg-green-500/10 text-green-500 border-green-500/20',
      'Pending': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'Rejected': 'bg-red-500/10 text-red-500 border-red-500/20'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium border ${classes[status]}`}>{status}</span>;
  };

  const getTransactionDisplayAmount = (tx) => {
    if (!tx) return '';
    return tx.crypto_type ? `${Number(tx.amount || 0).toFixed(8)} ${tx.crypto_type}` : `$${Number(tx.amount || 0).toFixed(2)}`;
  };

  const openTransactionDetails = (tx) => setSelectedTransaction(tx);

  const handleTransactionKeyDown = (event, tx) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openTransactionDetails(tx);
    }
  };

  const Sidebar = () => (
    <>
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card rounded-lg border border-border"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-button"
      >
        <svg className="w-6 h-6 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {mobileMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className={`w-64 exchange-sidebar border-r border-white/10 h-screen fixed left-0 top-0 flex flex-col z-40 transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="p-6 border-b border-white/10">
           <img 
                src="/logos/intellfinancial.png" 
                alt="Crystal Finance" 
                className="h-20 md:h-20 lg:h-24 w-auto"
              />
          <p className="text-xs text-muted-foreground mt-2 truncate">{user?.email}</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'overview' ? 'bg-[#f0b90b] text-slate-950 shadow-lg shadow-yellow-500/20' : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
            data-testid="nav-overview"
          >
            <FaHome />
            <span>Overview</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('trade'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'trade' ? 'bg-[#f0b90b] text-slate-950 shadow-lg shadow-yellow-500/20' : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
            data-testid="nav-trade"
          >
            <FaExchangeAlt />
            <span>Buy & Sell</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('wallets'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'wallets' ? 'bg-[#f0b90b] text-slate-950 shadow-lg shadow-yellow-500/20' : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
            data-testid="nav-wallets"
          >
            <FaWallet />
            <span>Wallets</span>
          </button>

          <button
            onClick={() => { setActiveTab('withdraw'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'withdraw' ? 'bg-[#f0b90b] text-slate-950 shadow-lg shadow-yellow-500/20' : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
            data-testid="nav-withdraw"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>Withdraw</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('cards'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'cards' ? 'bg-[#f0b90b] text-slate-950 shadow-lg shadow-yellow-500/20' : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
            data-testid="nav-cards"
          >
            <FaCreditCard />
            <span>Cards</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('history'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'history' ? 'bg-[#f0b90b] text-slate-950 shadow-lg shadow-yellow-500/20' : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
            data-testid="nav-history"
          >
            <FaHistory />
            <span>History</span>
          </button>
        </nav>
        
        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            data-testid="nav-logout"
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen exchange-workspace">
      <Sidebar />
      
      <div className="lg:ml-64 p-4 lg:p-8 pt-16 lg:pt-8">
        <div className="max-w-7xl mx-auto relative">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full exchange-pill px-3 py-1 text-xs text-muted-foreground mb-4">
                    <span className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_14px_rgba(34,197,94,0.9)]"></span>
                    Live portfolio terminal
                  </div>
                  <h2 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-2" data-testid="dashboard-heading">
                    Portfolio Overview
                  </h2>
                  <p className="text-muted-foreground">Welcome back, {user?.first_name}. Track balances, P/L, and live market movement.</p>
                </div>
                <div className="grid w-full grid-cols-3 gap-1 rounded-2xl exchange-pill p-1.5 text-center sm:w-auto sm:gap-2 sm:p-2">
                  {balances.prices.map(price => (
                    <div key={price.symbol} className="min-w-0 rounded-xl px-2 py-2 sm:px-3">
                      <p className="truncate text-[11px] text-muted-foreground sm:text-xs">{price.symbol}</p>
                      <p className="truncate text-xs font-semibold tabular-nums text-foreground sm:text-sm">${price.price.toFixed(price.symbol === 'USDT' ? 4 : 2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Portfolio Command Center */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <Card className="exchange-card overflow-hidden border-yellow-500/20 p-5 sm:p-7 lg:p-8 xl:col-span-7 min-h-[260px] flex flex-col justify-between">
                  <div>
                    <div className="relative mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
                      <p className="max-w-[14rem] text-xs uppercase leading-6 tracking-[0.32em] text-yellow-500 sm:max-w-none sm:text-sm sm:tracking-[0.22em]">Total Portfolio Value</p>
                      <span className="w-fit rounded-full border border-green-500/20 bg-green-500/10 px-3 py-1 text-xs font-medium text-green-500">Live</span>
                    </div>
                    <p className="relative max-w-full break-words font-heading text-[clamp(2.6rem,13vw,5.75rem)] font-bold leading-none tracking-tight text-foreground" data-testid="total-portfolio-value">
                      ${balances.total_portfolio_value.toFixed(2)}
                    </p>
                    <div className="relative mt-4 flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                      <span className={`w-fit rounded-full px-3 py-1 ${totalPnl >= 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                        {totalPnl >= 0 ? '+' : ''}{formatMoney(totalPnl)} all-time P/L
                      </span>
                      <span className="w-fit rounded-full bg-white/5 px-3 py-1 text-muted-foreground">
                        {balances.prices.length} tracked markets
                      </span>
                    </div>
                  </div>
                  <div className="relative mt-6 grid grid-cols-1 gap-3 sm:mt-8 sm:grid-cols-3">
                    {balances.prices.map(price => (
                      <div key={price.symbol} className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-3">
                        <div className="flex items-center justify-between gap-3 sm:block">
                          <p className="text-xs font-medium text-muted-foreground sm:mb-2">{price.symbol}</p>
                          <p className={`shrink-0 text-xs sm:hidden ${price.change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {price.change_24h >= 0 ? '+' : ''}{price.change_24h.toFixed(2)}%
                          </p>
                        </div>
                        <p className="mt-1 truncate text-xl font-semibold tabular-nums text-foreground sm:text-base">${price.price.toFixed(price.symbol === 'USDT' ? 4 : 2)}</p>
                        <p className={`mt-1 hidden text-xs sm:block ${price.change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {price.change_24h >= 0 ? '+' : ''}{price.change_24h.toFixed(2)}%
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>

                <div className="xl:col-span-5 grid grid-cols-1 gap-4">
                  <Card className="exchange-card metric-card p-5">
                    <p className="text-sm text-muted-foreground mb-2">Total Profit / Loss</p>
                    <p className={`relative text-4xl font-bold ${pnlTextClass(totalPnl)} ${pnlGlowClass(totalPnl)}`} data-testid="total-pnl-value">
                      {formatMoney(totalPnl)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Realized + open positions</p>
                  </Card>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="exchange-card metric-card p-5">
                      <p className="text-sm text-muted-foreground mb-2">Realized P/L</p>
                      <p className={`relative text-2xl font-bold ${pnlTextClass(realizedPnl)} ${pnlGlowClass(realizedPnl)}`}>
                        {formatMoney(realizedPnl)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">After selling</p>
                    </Card>
                    <Card className="exchange-card metric-card p-5">
                      <p className="text-sm text-muted-foreground mb-2">Unrealized P/L</p>
                      <p className={`relative text-2xl font-bold ${pnlTextClass(unrealizedPnl)} ${pnlGlowClass(unrealizedPnl)}`}>
                        {formatMoney(unrealizedPnl)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Live positions</p>
                    </Card>
                  </div>
                </div>
              </div>

              {/* Assets and Market Watch */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <div className="xl:col-span-8 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-xl font-semibold text-foreground">Asset Stack</h3>
                    <span className="text-xs text-muted-foreground">Balances, value, and open P/L</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="exchange-card metric-card p-5" data-testid="fiat-balance-card">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-muted-foreground">USD Cash</span>
                        <div className="p-2 rounded-full bg-blue-500/10">
                          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <p className="relative text-3xl font-bold text-foreground">${balances.fiat_balance.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground mt-2">Available for buying crypto</p>
                    </Card>

                    {['BTC', 'ETH', 'USDT'].map(crypto => {
                      const price = balances.prices.find(p => p.symbol === crypto);
                      const balance = balances[`${crypto.toLowerCase()}_balance`];
                      const value = balance * (price?.price || 0);
                      const assetPerformance = balances.portfolio_performance?.assets?.[crypto];
                      return (
                        <Card key={crypto} className="exchange-card metric-card p-5" data-testid={`${crypto.toLowerCase()}-balance-card`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-muted-foreground">{crypto}</span>
                            {CRYPTO_ICONS[crypto]}
                          </div>
                          <p className="relative text-2xl font-bold text-foreground mb-1">{balance.toFixed(6)}</p>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Value</span>
                            <span className="text-foreground">${value.toFixed(2)}</span>
                          </div>
                          <p className={`text-xs mt-2 ${price?.change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {price?.change_24h >= 0 ? '+' : ''}{price?.change_24h.toFixed(2)}% 24h
                          </p>
                          {assetPerformance && balance > 0 && (
                            <div className="mt-4 pt-4 border-t border-border/60 space-y-2">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Avg cost</span>
                                <span className="text-foreground">{formatMoney(assetPerformance.avg_cost)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Open P/L</span>
                                <span className={pnlTextClass(assetPerformance.unrealized_pnl)}>
                                  {formatMoney(assetPerformance.unrealized_pnl)}
                                </span>
                              </div>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>

                <Card className="exchange-card p-6 xl:col-span-4">
                  <div className="mb-4">
                    <h3 className="font-heading text-xl font-semibold text-foreground">Market Watch</h3>
                  </div>
                  <div className="space-y-3">
                    {balances.prices.map(price => (
                      <div key={price.symbol} className="market-row flex items-center justify-between p-4 rounded-xl">
                        <div className="flex items-center space-x-3">
                          {CRYPTO_ICONS[price.symbol]}
                          <div>
                            <p className="font-medium text-foreground">{price.name}</p>
                            <p className="text-sm text-muted-foreground">{price.symbol}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-foreground">${price.price.toFixed(price.symbol === 'USDT' ? 4 : 2)}</p>
                          <p className={`text-sm ${price.change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {price.change_24h >= 0 ? '+' : ''}{price.change_24h.toFixed(2)}% 24h
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Analytics and Activity */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <Card className="exchange-card p-6 xl:col-span-7">
                  <h3 className="font-medium text-foreground mb-4">Portfolio Performance</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={portfolioData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F0B90B" stopOpacity={0.35}/>
                          <stop offset="95%" stopColor="#F0B90B" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#1E293B' : '#E2E8F0'} />
                      <XAxis dataKey="name" stroke={theme === 'dark' ? '#94A3B8' : '#64748B'} />
                      <YAxis stroke={theme === 'dark' ? '#94A3B8' : '#64748B'} />
                      <Tooltip contentStyle={{ backgroundColor: theme === 'dark' ? '#0B1221' : '#FFFFFF', border: '1px solid #1E293B' }} />
                      <Area type="monotone" dataKey="value" stroke="#F0B90B" fillOpacity={1} fill="url(#colorValue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="exchange-card p-6 xl:col-span-5">
                  <h3 className="font-medium text-foreground mb-4">Asset Distribution</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: theme === 'dark' ? '#0B1221' : '#FFFFFF', border: '1px solid #1E293B' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              {/* Recent Transaction History */}
              <Card className="exchange-card p-4 sm:p-6">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="font-heading text-lg font-semibold text-foreground sm:text-xl">Recent Transactions</h3>
                  <button onClick={() => setActiveTab('history')} className="shrink-0 text-xs text-yellow-500 hover:text-yellow-400">View all</button>
                </div>
                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <FaHistory className="mx-auto text-muted-foreground mb-3" size={32} />
                    <p className="text-muted-foreground">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.slice(0, 5).map((tx) => {
                      const isCredit = tx.type === 'Buy' || tx.type === 'Deposit' || tx.type === 'Sell';
                      const displayAmount = tx.crypto_type 
                        ? `${tx.amount.toFixed(8)} ${tx.crypto_type}` 
                        : `$${tx.amount.toFixed(2)}`;
                      
                      return (
                        <div key={tx.id} role="button" tabIndex={0} onClick={() => openTransactionDetails(tx)} onKeyDown={(event) => handleTransactionKeyDown(event, tx)} className="market-row flex cursor-pointer flex-col gap-3 rounded-xl p-4 transition-transform hover:-translate-y-0.5 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex w-full min-w-0 items-start gap-3 sm:flex-1">
                            <div className={`shrink-0 p-2 rounded-full ${isCredit ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                              {isCredit ? (
                                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="break-words font-medium text-foreground">{tx.type} {tx.crypto_type || ''}</p>
                              <p className="break-words text-sm text-muted-foreground">{tx.description}</p>
                              {tx.type === 'Sell' && tx.realized_pnl !== null && tx.realized_pnl !== undefined && (
                                <p className={`text-xs font-medium ${pnlTextClass(tx.realized_pnl)}`}>
                                  Realized P/L: {formatMoney(tx.realized_pnl)}
                                </p>
                              )}
                              <p className="break-words text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                              <p className="text-[11px] font-medium text-yellow-500/80">Tap to view details</p>
                            </div>
                          </div>
                          <div className="flex w-full items-center justify-between gap-3 border-t border-white/10 pt-3 sm:block sm:w-auto sm:border-t-0 sm:pt-0 sm:text-right">
                            <p className={`whitespace-nowrap font-medium ${isCredit ? 'text-green-500' : 'text-red-500'}`}>
                              {isCredit ? '+' : '-'}{displayAmount}
                            </p>
                            <div className="shrink-0 sm:mt-1">{getStatusBadge(tx.status)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Trade Tab */}
          {activeTab === 'trade' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="inline-flex w-fit items-center gap-2 rounded-full exchange-pill px-3 py-1 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-yellow-500"></span>
                  Spot desk
                </div>
                <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Buy & Sell Crypto</h2>
                <p className="text-muted-foreground">Professional order tickets with live pricing, fees, and estimated P/L.</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="trade-ticket rounded-2xl p-4 sm:p-6">
                  <h3 className="font-medium text-foreground mb-4 flex items-center">
                    <div className="p-2 rounded-full bg-green-500/10 mr-3">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    Buy Crypto
                  </h3>
                  <form onSubmit={handleBuy} className="space-y-4">
                    <div>
                      <Label>Select Cryptocurrency</Label>
                      <Select value={buyForm.crypto_type} onValueChange={(v) => setBuyForm({...buyForm, crypto_type: v, amount: ''})}>
                        <SelectTrigger data-testid="buy-crypto-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BTC">
                            <div className="flex items-center space-x-2">
                              <SiBitcoin className="text-orange-500" />
                              <span>Bitcoin (BTC)</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="ETH">
                            <div className="flex items-center space-x-2">
                              <SiEthereum className="text-blue-500" />
                              <span>Ethereum (ETH)</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="USDT">
                            <div className="flex items-center space-x-2">
                              <SiTether className="text-green-500" />
                              <span>Tether (USDT)</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Label>Amount</Label>
                        <div className="flex w-full overflow-hidden rounded-md border border-border sm:w-auto">
                          <button
                            type="button"
                            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors sm:flex-none sm:py-1 ${buyForm.inputType === 'crypto' ? 'bg-[#f0b90b] text-slate-950' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
                            onClick={() => setBuyForm({...buyForm, inputType: 'crypto', amount: ''})}
                            data-testid="buy-input-crypto-toggle"
                          >
                            {buyForm.crypto_type}
                          </button>
                          <button
                            type="button"
                            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors sm:flex-none sm:py-1 ${buyForm.inputType === 'usd' ? 'bg-[#f0b90b] text-slate-950' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
                            onClick={() => setBuyForm({...buyForm, inputType: 'usd', amount: ''})}
                            data-testid="buy-input-usd-toggle"
                          >
                            USD
                          </button>
                        </div>
                      </div>
                      <Input
                        type="number"
                        step={buyForm.inputType === 'crypto' ? '0.00000001' : '0.01'}
                        value={buyForm.amount}
                        onChange={(e) => setBuyForm({...buyForm, amount: e.target.value})}
                        placeholder={buyForm.inputType === 'crypto' ? '0.00000000' : '0.00'}
                        required
                        data-testid="buy-amount-input"
                      />
                      <div className="mt-2 grid grid-cols-4 gap-2">
                        {QUICK_PERCENTAGES.map(({ label, pct }) => (
                          <button
                            key={label}
                            type="button"
                            className="rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground active:scale-[0.98]"
                            onClick={() => setBuyPercentage(pct)}
                            data-testid={`buy-pct-${label.toLowerCase()}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {buyForm.amount && balances.prices.find(p => p.symbol === buyForm.crypto_type) && (() => {
                        const price = balances.prices.find(p => p.symbol === buyForm.crypto_type).price;
                        const amt = parseFloat(buyForm.amount);
                        if (buyForm.inputType === 'usd') {
                          const cryptoAmt = amt / (price * 1.01);
                          return <p className="text-xs text-muted-foreground mt-1">You'll receive ≈ {cryptoAmt.toFixed(8)} {buyForm.crypto_type} (incl. 1% fee)</p>;
                        }
                        return <p className="text-xs text-muted-foreground mt-1">≈ ${(amt * price * 1.01).toFixed(2)} USD (incl. 1% fee)</p>;
                      })()}
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <p className="text-sm text-muted-foreground">Available: ${balances.fiat_balance.toFixed(2)}</p>
                    </div>
                    <Button type="submit" className="w-full bg-[#f0b90b] text-slate-950 hover:bg-[#d8a600]" disabled={loading} data-testid="buy-submit-button">
                      {loading ? 'Processing...' : 'Buy ' + buyForm.crypto_type}
                    </Button>
                  </form>
                </Card>

                <Card className="trade-ticket rounded-2xl p-4 sm:p-6">
                  <h3 className="font-medium text-foreground mb-4 flex items-center">
                    <div className="p-2 rounded-full bg-red-500/10 mr-3">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </div>
                    Sell Crypto
                  </h3>
                  <form onSubmit={handleSell} className="space-y-4">
                    <div>
                      <Label>Select Cryptocurrency</Label>
                      <Select value={sellForm.crypto_type} onValueChange={(v) => setSellForm({...sellForm, crypto_type: v, amount: ''})}>
                        <SelectTrigger data-testid="sell-crypto-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BTC">
                            <div className="flex items-center space-x-2">
                              <SiBitcoin className="text-orange-500" />
                              <span>Bitcoin (BTC)</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="ETH">
                            <div className="flex items-center space-x-2">
                              <SiEthereum className="text-blue-500" />
                              <span>Ethereum (ETH)</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="USDT">
                            <div className="flex items-center space-x-2">
                              <SiTether className="text-green-500" />
                              <span>Tether (USDT)</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Label>Amount</Label>
                        <div className="flex w-full overflow-hidden rounded-md border border-border sm:w-auto">
                          <button
                            type="button"
                            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors sm:flex-none sm:py-1 ${sellForm.inputType === 'crypto' ? 'bg-[#f0b90b] text-slate-950' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
                            onClick={() => setSellForm({...sellForm, inputType: 'crypto', amount: ''})}
                            data-testid="sell-input-crypto-toggle"
                          >
                            {sellForm.crypto_type}
                          </button>
                          <button
                            type="button"
                            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors sm:flex-none sm:py-1 ${sellForm.inputType === 'usd' ? 'bg-[#f0b90b] text-slate-950' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
                            onClick={() => setSellForm({...sellForm, inputType: 'usd', amount: ''})}
                            data-testid="sell-input-usd-toggle"
                          >
                            USD
                          </button>
                        </div>
                      </div>
                      <Input
                        type="number"
                        step={sellForm.inputType === 'crypto' ? '0.00000001' : '0.01'}
                        value={sellForm.amount}
                        onChange={(e) => setSellForm({...sellForm, amount: e.target.value})}
                        placeholder={sellForm.inputType === 'crypto' ? '0.00000000' : '0.00'}
                        required
                        data-testid="sell-amount-input"
                      />
                      <div className="mt-2 grid grid-cols-4 gap-2">
                        {QUICK_PERCENTAGES.map(({ label, pct }) => (
                          <button
                            key={label}
                            type="button"
                            className="rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground active:scale-[0.98]"
                            onClick={() => setSellPercentage(pct)}
                            data-testid={`sell-pct-${label.toLowerCase()}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {sellForm.amount && balances.prices.find(p => p.symbol === sellForm.crypto_type) && (() => {
                        const price = balances.prices.find(p => p.symbol === sellForm.crypto_type).price;
                        const amt = parseFloat(sellForm.amount);
                        if (sellForm.inputType === 'usd') {
                          const cryptoAmt = amt / (price * 0.99);
                          return <p className="text-xs text-muted-foreground mt-1">You'll sell ≈ {cryptoAmt.toFixed(8)} {sellForm.crypto_type} (after 1% fee)</p>;
                        }
                        return <p className="text-xs text-muted-foreground mt-1">≈ ${(amt * price * 0.99).toFixed(2)} USD (after 1% fee)</p>;
                      })()}
                      {(() => {
                        const estimate = getSellEstimate();
                        if (!estimate) return null;
                        return (
                          <div className="mt-2 rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-3 text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Estimated sale value</span>
                              <span className="text-foreground">{formatMoney(estimate.netValue)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Average cost</span>
                              <span className="text-foreground">{formatMoney(estimate.avgCost)}</span>
                            </div>
                            <div className="flex justify-between font-medium">
                              <span className="text-muted-foreground">Estimated P/L</span>
                              <span className={pnlTextClass(estimate.estimatedPnl)}>
                                {formatMoney(estimate.estimatedPnl)}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Available: {balances[`${sellForm.crypto_type.toLowerCase()}_balance`]?.toFixed(8)} {sellForm.crypto_type}
                      </p>
                    </div>
                    <Button type="submit" className="w-full bg-red-500 text-white hover:bg-red-600" disabled={loading} data-testid="sell-submit-button">
                      {loading ? 'Processing...' : 'Sell ' + sellForm.crypto_type}
                    </Button>
                  </form>
                </Card>
              </div>
            </div>
          )}

          {/* Wallets Tab */}
          {activeTab === 'wallets' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="inline-flex w-fit items-center gap-2 rounded-full exchange-pill px-3 py-1 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-green-500"></span>
                  Secure wallet center
                </div>
                <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground">My Wallets</h2>
                <p className="text-muted-foreground">View addresses, copy wallet details, and transfer crypto from one clean workspace.</p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <div className="xl:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['BTC', 'ETH', 'USDT'].map(crypto => {
                    const wallet = user[`${crypto.toLowerCase()}_wallet`];
                    const balance = balances[`${crypto.toLowerCase()}_balance`];
                    const price = balances.prices.find(p => p.symbol === crypto)?.price || 0;
                    return (
                      <Card key={crypto} className="exchange-card metric-card p-5">
                        <div className="relative flex items-start justify-between gap-4 mb-5">
                          <div className="flex items-center gap-3">
                            <div className="rounded-2xl bg-white/5 p-3">{CRYPTO_ICONS[crypto]}</div>
                            <div>
                              <h3 className="font-semibold text-foreground">{crypto} Wallet</h3>
                              <p className="text-xs text-muted-foreground">≈ ${(balance * price).toFixed(2)} USD</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="relative"
                            onClick={() => { setSelectedCrypto(crypto); setShowQR(true); }}
                            data-testid={`show-qr-${crypto.toLowerCase()}`}
                          >
                            <FaQrcode className="mr-2" />
                            QR
                          </Button>
                        </div>
                        <div className="relative space-y-4">
                          <div className="rounded-2xl fintech-soft-row p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Balance</span>
                              <span className="text-xs text-muted-foreground">{crypto}</span>
                            </div>
                            <p className="text-2xl font-semibold text-foreground">{balance.toFixed(8)}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Wallet address</p>
                            <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                              <p className="flex-1 truncate font-mono text-sm text-foreground">{wallet}</p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyToClipboard(wallet)}
                                data-testid={`copy-${crypto.toLowerCase()}-wallet`}
                              >
                                <FaCopy />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                <Card className="fintech-panel rounded-2xl p-6 xl:col-span-5 h-fit">
                  <div className="mb-6">
                    <h3 className="font-heading text-2xl font-semibold text-foreground">Transfer Crypto</h3>
                    <p className="text-sm text-muted-foreground mt-1">Choose an asset, enter amount, and send to an external wallet.</p>
                  </div>
                  <form onSubmit={handleTransfer} className="space-y-5">
                    <div>
                      <Label>Cryptocurrency</Label>
                      <Select value={transferForm.crypto_type} onValueChange={(v) => setTransferForm({...transferForm, crypto_type: v, amount: ''})}>
                        <SelectTrigger data-testid="transfer-crypto-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BTC"><div className="flex items-center space-x-2"><SiBitcoin className="text-orange-500" /><span>Bitcoin (BTC)</span></div></SelectItem>
                          <SelectItem value="ETH"><div className="flex items-center space-x-2"><SiEthereum className="text-blue-500" /><span>Ethereum (ETH)</span></div></SelectItem>
                          <SelectItem value="USDT"><div className="flex items-center space-x-2"><SiTether className="text-green-500" /><span>Tether (USDT)</span></div></SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Available Balance</span>
                        <span className="text-sm font-semibold text-foreground" data-testid="transfer-available-balance">
                          {(balances[`${transferForm.crypto_type.toLowerCase()}_balance`] || 0).toFixed(8)} {transferForm.crypto_type}
                        </span>
                      </div>
                      {balances.prices.find(p => p.symbol === transferForm.crypto_type) && (
                        <p className="text-xs text-muted-foreground text-right mt-1">
                          ≈ ${((balances[`${transferForm.crypto_type.toLowerCase()}_balance`] || 0) * balances.prices.find(p => p.symbol === transferForm.crypto_type).price).toFixed(2)} USD
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Amount</Label>
                        <div className="flex rounded-md overflow-hidden border border-border">
                          <button type="button" className={`px-3 py-1 text-xs font-medium transition-colors ${transferForm.inputType === 'crypto' ? 'bg-[#f0b90b] text-slate-950' : 'bg-muted text-muted-foreground hover:bg-accent'}`} onClick={() => setTransferForm({...transferForm, inputType: 'crypto', amount: ''})} data-testid="transfer-input-crypto-toggle">{transferForm.crypto_type}</button>
                          <button type="button" className={`px-3 py-1 text-xs font-medium transition-colors ${transferForm.inputType === 'usd' ? 'bg-[#f0b90b] text-slate-950' : 'bg-muted text-muted-foreground hover:bg-accent'}`} onClick={() => setTransferForm({...transferForm, inputType: 'usd', amount: ''})} data-testid="transfer-input-usd-toggle">USD</button>
                        </div>
                      </div>
                      <Input type="number" step={transferForm.inputType === 'crypto' ? '0.00000001' : '0.01'} value={transferForm.amount} onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})} placeholder={transferForm.inputType === 'crypto' ? '0.00000000' : '0.00'} required data-testid="transfer-amount-input" />
                      <div className="flex gap-2 mt-2">
                        {QUICK_PERCENTAGES.map(({ label, pct }) => (
                          <button key={label} type="button" className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground" onClick={() => {
                            const avail = balances[`${transferForm.crypto_type.toLowerCase()}_balance`] || 0;
                            const fee = NETWORK_FEES[transferForm.crypto_type] || 0;
                            const maxTransferable = Math.max(0, avail - fee);
                            const amt = maxTransferable * pct;
                            if (transferForm.inputType === 'usd') {
                              const price = balances.prices.find(p => p.symbol === transferForm.crypto_type)?.price || 1;
                              setTransferForm({...transferForm, amount: (amt * price).toFixed(2)});
                            } else {
                              setTransferForm({...transferForm, amount: amt.toFixed(8)});
                            }
                          }} data-testid={`transfer-pct-${label.toLowerCase()}`}>{label}</button>
                        ))}
                      </div>
                      {transferForm.amount && balances.prices.find(p => p.symbol === transferForm.crypto_type) && (() => {
                        const price = balances.prices.find(p => p.symbol === transferForm.crypto_type).price;
                        const amt = parseFloat(transferForm.amount);
                        const fee = NETWORK_FEES[transferForm.crypto_type] || 0;
                        if (transferForm.inputType === 'usd') {
                          const cryptoAmt = amt / price;
                          return <div className="mt-3 rounded-xl bg-white/5 p-3 text-xs space-y-1"><p className="text-muted-foreground">You'll transfer ≈ {cryptoAmt.toFixed(8)} {transferForm.crypto_type}</p><p className="text-yellow-500">Network fee: {fee} {transferForm.crypto_type} (≈ ${(fee * price).toFixed(2)})</p><p className="text-muted-foreground">Total deducted: {(cryptoAmt + fee).toFixed(8)} {transferForm.crypto_type}</p></div>;
                        }
                        return <div className="mt-3 rounded-xl bg-white/5 p-3 text-xs space-y-1"><p className="text-muted-foreground">≈ ${(amt * price).toFixed(2)} USD</p><p className="text-yellow-500">Network fee: {fee} {transferForm.crypto_type} (≈ ${(fee * price).toFixed(2)})</p><p className="text-muted-foreground">Total deducted: {(amt + fee).toFixed(8)} {transferForm.crypto_type}</p></div>;
                      })()}
                    </div>

                    <div>
                      <Label>Recipient Wallet Address</Label>
                      <Input value={transferForm.wallet_address} onChange={(e) => setTransferForm({...transferForm, wallet_address: e.target.value})} placeholder={transferForm.crypto_type === 'BTC' ? '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa' : '0x742d35Cc6634C0532925a3b844Bc9e7595f2bD18'} required data-testid="transfer-wallet-input" />
                    </div>
                    <Button type="submit" className="w-full bg-[#f0b90b] text-slate-950 hover:bg-[#d8a600]" disabled={loading} data-testid="transfer-submit-button">
                      {loading ? 'Processing...' : 'Submit Transfer'}
                    </Button>
                  </form>
                </Card>
              </div>
            </div>
          )}

          {/* Withdraw Tab */}
          {activeTab === 'withdraw' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="inline-flex w-fit items-center gap-2 rounded-full exchange-pill px-3 py-1 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                  Withdrawal desk
                </div>
                <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground">Withdraw Funds</h2>
                <p className="text-muted-foreground">Send available USD balance to a linked payment card.</p>
              </div>

              {paymentMethods.length === 0 ? (
                <Card className="fintech-panel rounded-2xl p-12 text-center">
                  <FaCreditCard className="mx-auto text-muted-foreground mb-4" size={64} />
                  <h3 className="text-xl font-medium text-foreground mb-2">No Cards Linked</h3>
                  <p className="text-muted-foreground mb-6">Link a card first to enable withdrawals.</p>
                  <Button onClick={() => setActiveTab('cards')} className="bg-[#f0b90b] text-slate-950 hover:bg-[#d8a600]" data-testid="link-card-button"><FaCreditCard className="mr-2" />Link a Card</Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                  <div className="xl:col-span-7 space-y-4">
                    <h3 className="font-heading text-xl font-semibold text-foreground">Linked Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {paymentMethods.map(pm => (
                        <div key={pm.id} className="fintech-card-visual rounded-2xl p-6 text-white" data-testid={`withdraw-card-${pm.id}`}>
                          <div className="relative z-10 flex flex-col min-h-[190px] justify-between">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-xs text-white/60 uppercase tracking-[0.18em]">Cardholder</p>
                                <p className="font-semibold text-lg mt-1">{pm.card_holder}</p>
                              </div>
                              <FaCreditCard size={30} className="text-white/80" />
                            </div>
                            <p className="text-2xl font-mono tracking-widest">•••• •••• •••• {pm.card_number.slice(-4)}</p>
                            <div className="flex justify-between items-end">
                              <div><p className="text-xs text-white/60 uppercase tracking-[0.18em]">Expires</p><p className="font-medium">{pm.expiry_date}</p></div>
                              <p className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold tracking-wider">{getCardType(pm.card_number)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Card className="fintech-panel rounded-2xl p-6 xl:col-span-5 h-fit">
                    <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">Withdraw to Card</h3>
                    <p className="text-sm text-muted-foreground mb-6">Choose a linked card and amount.</p>
                    <form onSubmit={handleWithdraw} className="space-y-5">
                      <div>
                        <Label>Select Card</Label>
                        <Select value={withdrawForm.card_id} onValueChange={(v) => setWithdrawForm({...withdrawForm, card_id: v})}>
                          <SelectTrigger data-testid="withdraw-card-select"><SelectValue placeholder="Select card" /></SelectTrigger>
                          <SelectContent>{paymentMethods.map(pm => (<SelectItem key={pm.id} value={pm.id}>{pm.card_holder} - •••• {pm.card_number.slice(-4)}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Withdrawal Amount (USD)</Label>
                        <Input type="number" step="0.01" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})} placeholder="0.00" required data-testid="withdraw-amount-input" />
                      </div>
                      <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-4">
                        <div className="flex justify-between items-center mb-2"><span className="text-sm text-muted-foreground">Available Balance</span><span className="font-semibold text-foreground">${balances.fiat_balance.toFixed(2)}</span></div>
                        <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Processing Fee</span><span className="text-sm text-muted-foreground">$0.00</span></div>
                      </div>
                      <Button type="submit" className="w-full bg-[#f0b90b] text-slate-950 hover:bg-[#d8a600]" disabled={loading} data-testid="withdraw-submit-button">{loading ? 'Processing...' : 'Withdraw Funds'}</Button>
                    </form>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Cards Tab */}
          {activeTab === 'cards' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="inline-flex w-fit items-center gap-2 rounded-full exchange-pill px-3 py-1 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-yellow-500"></span>
                  Card vault
                </div>
                <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground">Payment Methods</h2>
                <p className="text-muted-foreground">Add, review, and manage linked cards for withdrawals.</p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <div className="xl:col-span-7 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-xl font-semibold text-foreground">Your Cards</h3>
                    <span className="text-xs text-muted-foreground">{paymentMethods.length} linked</span>
                  </div>
                  {paymentMethods.length === 0 ? (
                    <Card className="fintech-panel rounded-2xl p-10 text-center">
                      <FaCreditCard className="mx-auto text-muted-foreground mb-3" size={48} />
                      <p className="text-foreground font-medium">No cards linked yet</p>
                      <p className="text-sm text-muted-foreground mt-2">Add a card to enable withdrawals.</p>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {paymentMethods.map(pm => (
                        <div key={pm.id} className="relative" data-testid={`payment-method-${pm.id}`}>
                          <div className="fintech-card-visual rounded-2xl p-6 text-white transition-all duration-300 hover:-translate-y-1">
                            <div className="relative z-10 flex min-h-[210px] flex-col justify-between">
                              <div className="flex justify-between items-start">
                                <div><p className="text-xs text-white/60 uppercase tracking-[0.18em]">Cardholder</p><p className="font-semibold text-lg mt-1">{pm.card_holder}</p></div>
                                <FaCreditCard size={30} className="text-white/80" />
                              </div>
                              <p className="text-2xl font-mono tracking-widest">•••• •••• •••• {pm.card_number.slice(-4)}</p>
                              <div className="flex justify-between items-center">
                                <div><p className="text-xs text-white/60 uppercase tracking-[0.18em]">Expires</p><p className="font-medium text-sm">{pm.expiry_date}</p></div>
                                <div className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold">{getCardType(pm.card_number)}</div>
                              </div>
                            </div>
                          </div>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteCard(pm.id)} disabled={loading} className="absolute top-3 right-3 z-20" data-testid={`delete-card-${pm.id}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Card className="fintech-panel rounded-2xl p-6 xl:col-span-5 h-fit">
                  <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">Add New Card</h3>
                  <p className="text-sm text-muted-foreground mb-6">Link a card for faster withdrawals.</p>
                  <form onSubmit={handleAddCard} className="space-y-5">
                    <div><Label>Card Number</Label><Input value={formatCardNumber(cardForm.card_number)} onChange={(e) => setCardForm({...cardForm, card_number: e.target.value})} placeholder="1234 5678 9012 3456" maxLength={19} required data-testid="card-number-input" /></div>
                    <div><Label>Cardholder Name</Label><Input value={cardForm.card_holder} onChange={(e) => setCardForm({...cardForm, card_holder: e.target.value})} placeholder="JOHN DOE" required data-testid="card-holder-input" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Expiry Date</Label><Input value={formatExpiry(cardForm.expiry_date)} onChange={(e) => setCardForm({...cardForm, expiry_date: e.target.value})} placeholder="MM/YY" maxLength={5} required data-testid="card-expiry-input" /></div>
                      <div><Label>CVV</Label><Input value={cardForm.cvv} onChange={(e) => setCardForm({...cardForm, cvv: e.target.value.replace(/\D/g, '').slice(0, 3)})} placeholder="123" maxLength={3} required data-testid="card-cvv-input" /></div>
                    </div>
                    <Button type="submit" className="w-full bg-[#f0b90b] text-slate-950 hover:bg-[#d8a600]" disabled={loading} data-testid="add-card-button">Add Card</Button>
                  </form>
                </Card>
              </div>
            </div>
          )}
          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-6">
              <h2 className="font-heading text-3xl font-semibold text-foreground">Transaction History</h2>
              
              <Card className="exchange-card p-4 sm:p-6">
                {transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <FaHistory className="mx-auto text-muted-foreground mb-3" size={48} />
                    <p className="text-muted-foreground">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => {
                      const isCredit = tx.type === 'Buy' || tx.type === 'Deposit' || tx.type === 'Sell';
                      const displayAmount = tx.crypto_type 
                        ? `${tx.amount.toFixed(8)} ${tx.crypto_type}` 
                        : `$${tx.amount.toFixed(2)}`;
                      
                      return (
                        <div key={tx.id} role="button" tabIndex={0} onClick={() => openTransactionDetails(tx)} onKeyDown={(event) => handleTransactionKeyDown(event, tx)} className="market-row flex cursor-pointer flex-col gap-3 rounded-xl p-4 transition-transform hover:-translate-y-0.5 sm:flex-row sm:items-center sm:justify-between" data-testid={`transaction-${tx.id}`}>
                          <div className="flex w-full min-w-0 items-start gap-3 sm:flex-1">
                            <div className={`shrink-0 p-2 rounded-full ${isCredit ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                              {isCredit ? (
                                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="break-words font-medium text-foreground">{tx.type} {tx.crypto_type || ''}</p>
                              <p className="break-words text-sm text-muted-foreground">{tx.description}</p>
                              {tx.type === 'Sell' && tx.realized_pnl !== null && tx.realized_pnl !== undefined && (
                                <p className={`text-xs font-medium ${pnlTextClass(tx.realized_pnl)}`}>
                                  Realized P/L: {formatMoney(tx.realized_pnl)}
                                </p>
                              )}
                              <p className="break-words text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                              <p className="text-[11px] font-medium text-yellow-500/80">Tap to view details</p>
                            </div>
                          </div>
                          <div className="flex w-full items-center justify-between gap-3 border-t border-white/10 pt-3 sm:block sm:w-auto sm:border-t-0 sm:pt-0 sm:text-right">
                            <p className={`whitespace-nowrap font-medium ${isCredit ? 'text-green-500' : 'text-red-500'}`}>
                              {isCredit ? '+' : '-'}{displayAmount}
                            </p>
                            <div className="shrink-0 sm:mt-1">{getStatusBadge(tx.status)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Details Dialog */}
      <Dialog open={!!selectedTransaction} onOpenChange={(open) => !open && setSelectedTransaction(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span>Transaction Details</span>
              {selectedTransaction && getStatusBadge(selectedTransaction.status)}
            </DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Amount</p>
                <p className="mt-1 break-words text-2xl font-semibold text-foreground">
                  {getTransactionDisplayAmount(selectedTransaction)}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedTransaction.type} {selectedTransaction.crypto_type || 'FIAT'}
                </p>
              </div>

              <div className="space-y-3 rounded-2xl border border-white/10 bg-black/10 p-4">
                <div>
                  <p className="text-xs text-muted-foreground">Transaction ID</p>
                  <div className="mt-1 flex items-start gap-2">
                    <p className="min-w-0 flex-1 break-all font-mono text-sm text-foreground">{selectedTransaction.id}</p>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(selectedTransaction.id)}>
                      <FaCopy />
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Sender Name</p>
                    <p className="break-words text-sm text-foreground">{selectedTransaction.sender_name || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="break-words text-sm text-foreground">{new Date(selectedTransaction.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Description</p>
                  <p className="break-words text-sm text-foreground">{selectedTransaction.description || 'No description'}</p>
                </div>
                {selectedTransaction.sender_wallet && (
                  <div>
                    <p className="text-xs text-muted-foreground">Sender Wallet</p>
                    <p className="break-all font-mono text-sm text-foreground">{selectedTransaction.sender_wallet}</p>
                  </div>
                )}
                {selectedTransaction.recipient_wallet && (
                  <div>
                    <p className="text-xs text-muted-foreground">Recipient Wallet</p>
                    <p className="break-all font-mono text-sm text-foreground">{selectedTransaction.recipient_wallet}</p>
                  </div>
                )}
                {selectedTransaction.transaction_hash && (
                  <div>
                    <p className="text-xs text-muted-foreground">Hash ID</p>
                    <div className="mt-1 flex items-start gap-2">
                      <p className="min-w-0 flex-1 break-all font-mono text-sm text-foreground">{selectedTransaction.transaction_hash}</p>
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(selectedTransaction.transaction_hash)}>
                        <FaCopy />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedTransaction.type === 'Sell' && selectedTransaction.realized_pnl !== null && selectedTransaction.realized_pnl !== undefined && (
                  <div>
                    <p className="text-xs text-muted-foreground">Realized P/L</p>
                    <p className={`text-sm font-semibold ${pnlTextClass(selectedTransaction.realized_pnl)}`}>
                      {formatMoney(selectedTransaction.realized_pnl)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              {CRYPTO_ICONS[selectedCrypto]}
              <span>{selectedCrypto} Wallet Address</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center p-4 bg-white rounded-lg">
              <QRCode value={user?.[`${selectedCrypto.toLowerCase()}_wallet`] || ''} size={200} />
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Address</p>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-foreground font-mono truncate flex-1">
                  {user?.[`${selectedCrypto.toLowerCase()}_wallet`]}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(user?.[`${selectedCrypto.toLowerCase()}_wallet`] || '')}
                >
                  <FaCopy />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
